import mapboxgl from 'mapbox-gl'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import 'mapbox-gl/dist/mapbox-gl.css'
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import { App } from 'vue'
import { CURRENT_CONFIG } from '/@/api/http/config'
import store from '/@/store'
import { postElementsReq } from '/@/api/layer'
import { generatePoint } from '/@/utils/genjson'
import { MapDoodleColor, MapElementEnum } from '/@/constants/map'
import { uuidv4 } from '/@/utils/uuid'
import EventBus from '/@/event-bus'
import { useGMapCover } from '/@/hooks/use-g-map-cover'
import { ensureIgnDemSource, IGN_DEM_SOURCE } from '/@/hooks/use-ign-terrain'

// ── 3D scenery (terrain relief + sky + 3D buildings) ─────────────────────────
// Exaggeration is shared so it survives style changes (setStyle wipes the terrain).
// Default 1.0 = relief at true 1:1 scale, so the rendered ground, pins, lines and the
// "m ASL" altitudes (queried with exaggerated:false) all agree. The Relief slider can
// still inflate it on demand. Elevated lines drape on the (exaggerated) terrain natively
// via line-elevation-reference:ground, so they track the slider automatically.
let terrainExaggeration = 1.0

// Source de terrain active : DEM Mapbox (~30 m, monde) ou DEM IGN haute résolution
// (RGE ALTI / LiDAR HD, France). Persistée pour survivre aux rechargements.
export type TerrainSource = 'mapbox' | 'ign'
const TERRAIN_PREF_KEY = 'terrain-source'
const MAPBOX_DEM_SOURCE = 'mapbox-dem'
let terrainSource: TerrainSource =
  (localStorage.getItem(TERRAIN_PREF_KEY) as TerrainSource) === 'ign' ? 'ign' : 'mapbox'

function activeDemSourceId (): string {
  return terrainSource === 'ign' ? IGN_DEM_SOURCE : MAPBOX_DEM_SOURCE
}

export function getTerrainSource (): TerrainSource {
  return terrainSource
}

export function getTerrainExaggeration (): number {
  return terrainExaggeration
}

function addMapboxDem (m: mapboxgl.Map): void {
  if (!m.getSource(MAPBOX_DEM_SOURCE)) {
    m.addSource(MAPBOX_DEM_SOURCE, {
      type: 'raster-dem',
      url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
      tileSize: 512,
      maxzoom: 14,
    } as any)
  }
}

// Applique la source de terrain active (ajoute la source manquante si besoin).
// Le DEM IGN passe par un Service Worker (async) → on ne pose le terrain qu'une
// fois la source prête, avec repli automatique sur le DEM Mapbox en cas d'échec.
function applyTerrain (m: mapboxgl.Map): void {
  if (terrainSource === 'ign') {
    ensureIgnDemSource(m)
      .then(() => m.setTerrain({ source: IGN_DEM_SOURCE, exaggeration: terrainExaggeration } as any))
      .catch(err => {
        console.warn('[terrain] DEM IGN indisponible, repli sur Mapbox:', err)
        terrainSource = 'mapbox'
        try { localStorage.setItem(TERRAIN_PREF_KEY, 'mapbox') } catch (_) { /* mode privé */ }
        addMapboxDem(m)
        m.setTerrain({ source: MAPBOX_DEM_SOURCE, exaggeration: terrainExaggeration } as any)
      })
  } else {
    addMapboxDem(m)
    m.setTerrain({ source: MAPBOX_DEM_SOURCE, exaggeration: terrainExaggeration } as any)
  }
}

export function setTerrainExaggeration (map: mapboxgl.Map, value: number): void {
  terrainExaggeration = value
  if (map.getSource(activeDemSourceId())) {
    map.setTerrain({ source: activeDemSourceId(), exaggeration: value } as any)
  }
}

// Bascule entre le DEM Mapbox et le DEM IGN haute résolution.
export function setTerrainSource (map: mapboxgl.Map, src: TerrainSource): void {
  terrainSource = src
  try { localStorage.setItem(TERRAIN_PREF_KEY, src) } catch (_) { /* mode privé */ }
  applyTerrain(map)
}

// Adds (or re-adds) the DEM terrain, atmospheric sky and 3D building extrusions.
// Safe to call repeatedly — each piece is guarded by an existence check.
export function add3DScenery (m: mapboxgl.Map): void {
  // Terrain relief — source active (Mapbox ou IGN)
  applyTerrain(m)

  // Sky / atmosphere — only visible when the camera is pitched
  if (!m.getLayer('sky')) {
    m.addLayer({
      id: 'sky',
      type: 'sky',
      paint: {
        'sky-type': 'atmosphere',
        'sky-atmosphere-sun': [0.0, 90.0],
        'sky-atmosphere-sun-intensity': 15,
      },
    } as any)
  }

  // 3D buildings (toggle persisté)
  ensure3DBuildings(m)
}

// ── Bâtiments 3D (extrusions) ────────────────────────────────────────────────
// Disponibles uniquement sur les styles vectoriels exposant la source 'composite'
// (Satellite+routes, Plan, Randonnée…). Les styles satellite seuls n'ont pas de
// géométrie de bâtiments. Visibles seulement caméra inclinée (3D).
const BUILDINGS_PREF_KEY = 'buildings-3d'
let buildingsVisible: boolean = localStorage.getItem(BUILDINGS_PREF_KEY) !== '0' // défaut : affichés

export function getBuildingsVisible (): boolean {
  return buildingsVisible
}

// Ajoute la couche bâtiments si possible, puis applique la visibilité courante.
// Idempotent — sûr d'appeler après chaque changement de style.
function ensure3DBuildings (m: mapboxgl.Map): void {
  if (!m.getLayer('3d-buildings') && m.getSource('composite')) {
    const layers = m.getStyle().layers || []
    const firstSymbolId = layers.find(
      l => l.type === 'symbol' && (l.layout as any)?.['text-field']
    )?.id
    try {
      m.addLayer({
        id: '3d-buildings',
        source: 'composite',
        'source-layer': 'building',
        filter: ['==', 'extrude', 'true'],
        type: 'fill-extrusion',
        minzoom: 14,
        layout: { visibility: buildingsVisible ? 'visible' : 'none' },
        paint: {
          'fill-extrusion-color': '#aaa',
          'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 14, 0, 16, ['get', 'height']],
          'fill-extrusion-base': ['interpolate', ['linear'], ['zoom'], 14, 0, 16, ['get', 'min_height']],
          'fill-extrusion-opacity': 0.6,
        },
      } as any, firstSymbolId)
    } catch (e) {
      // Style has no building layer in 'composite' — ignore.
    }
  }
  if (m.getLayer('3d-buildings')) {
    m.setLayoutProperty('3d-buildings', 'visibility', buildingsVisible ? 'visible' : 'none')
  }
}

// Affiche / masque les bâtiments 3D (persisté).
export function setBuildingsVisible (map: mapboxgl.Map, visible: boolean): void {
  buildingsVisible = visible
  try { localStorage.setItem(BUILDINGS_PREF_KEY, visible ? '1' : '0') } catch (_) { /* mode privé */ }
  ensure3DBuildings(map)
}
// ─────────────────────────────────────────────────────────────────────────────

function makeCtrlButton (title: string, icon: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button')
  btn.type = 'button'
  btn.title = title
  btn.style.cssText = 'font-size:16px; line-height:1; display:flex; align-items:center; justify-content:center;'
  btn.innerHTML = icon
  btn.addEventListener('click', onClick)
  return btn
}

// ── Outil de mesure (linestring non persisté) ────────────────────────────────
// Trace une polyligne éphémère : clic = ajoute un sommet, double-clic = termine,
// Échap ou re-clic sur le bouton = efface. Rien n'est envoyé au backend.
const MEASURE_SOURCE = 'measure-tool'
const MEASURE_LINE_LAYER = 'measure-tool-line'
const MEASURE_POINT_LAYER = 'measure-tool-points'
const MEASURE_LABEL_SOURCE = 'measure-tool-labels'
const MEASURE_LABEL_LAYER = 'measure-tool-labels'

// Styles globaux de l'outil de mesure (boutons/popup créés en DOM hors Vue scope).
if (typeof document !== 'undefined' && !document.getElementById('measure-tool-style')) {
  const st = document.createElement('style')
  st.id = 'measure-tool-style'
  st.textContent = `
    .measure-btn-active { background:#ffcc00 !important; color:#000 !important; }
    .measure-popup .mapboxgl-popup-content { background:#1a1a1a; color:#fff; padding:4px 8px; border-radius:4px; box-shadow:0 1px 4px rgba(0,0,0,.5); }
    .measure-popup .mapboxgl-popup-tip { display:none; }
  `
  document.head.appendChild(st)
}

let measureActive = false
let measureFinalized = false
let measurePoints: [number, number][] = []
let measureCursor: [number, number] | null = null
let measurePopup: mapboxgl.Popup | null = null
let measureBtnEl: HTMLButtonElement | null = null
let measureClickHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null
let measureMoveHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null
let measureDblHandler: ((e: mapboxgl.MapMouseEvent) => void) | null = null
let measureKeyHandler: ((e: KeyboardEvent) => void) | null = null
let measureStyleHandler: (() => void) | null = null

function haversine (a: [number, number], b: [number, number]): number {
  const R = 6371000 // rayon terrestre moyen, m
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b[1] - a[1])
  const dLon = toRad(b[0] - a[0])
  const lat1 = toRad(a[1])
  const lat2 = toRad(b[1])
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

function pathLength (pts: [number, number][]): number {
  let d = 0
  for (let i = 1; i < pts.length; i++) d += haversine(pts[i - 1], pts[i])
  return d
}

function fmtDistance (m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`
}

function ensureMeasureLayers (map: mapboxgl.Map) {
  if (!map.getSource(MEASURE_SOURCE)) {
    map.addSource(MEASURE_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
  }
  if (!map.getLayer(MEASURE_LINE_LAYER)) {
    map.addLayer({
      id: MEASURE_LINE_LAYER,
      type: 'line',
      source: MEASURE_SOURCE,
      filter: ['==', '$type', 'LineString'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#ffcc00', 'line-width': 2, 'line-dasharray': [2, 1] },
    })
  }
  if (!map.getLayer(MEASURE_POINT_LAYER)) {
    map.addLayer({
      id: MEASURE_POINT_LAYER,
      type: 'circle',
      source: MEASURE_SOURCE,
      filter: ['==', '$type', 'Point'],
      paint: {
        'circle-radius': 4,
        'circle-color': '#ffcc00',
        'circle-stroke-color': '#000',
        'circle-stroke-width': 1,
      },
    })
  }
  if (!map.getSource(MEASURE_LABEL_SOURCE)) {
    map.addSource(MEASURE_LABEL_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } })
  }
  if (!map.getLayer(MEASURE_LABEL_LAYER)) {
    map.addLayer({
      id: MEASURE_LABEL_LAYER,
      type: 'symbol',
      source: MEASURE_LABEL_SOURCE,
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 12,
        'text-anchor': 'center',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': '#fff', 'text-halo-color': '#000', 'text-halo-width': 1.5 },
    })
  }
}

// Sommets committés + (si en cours de tracé) le point sous le curseur = élastique.
function measureDisplayPoints (): [number, number][] {
  return !measureFinalized && measureCursor ? [...measurePoints, measureCursor] : [...measurePoints]
}

function updateMeasureSource (map: mapboxgl.Map) {
  const pts = measureDisplayPoints()
  const features: any[] = measurePoints.map((c) => ({ type: 'Feature', geometry: { type: 'Point', coordinates: c } }))
  if (pts.length >= 2) features.push({ type: 'Feature', geometry: { type: 'LineString', coordinates: pts } })
  const src = map.getSource(MEASURE_SOURCE) as mapboxgl.GeoJSONSource | undefined
  src?.setData({ type: 'FeatureCollection', features })

  // Étiquette de distance au centre de chaque segment.
  const labels: any[] = []
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]
    const b = pts[i]
    const mid = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2]
    labels.push({ type: 'Feature', properties: { label: fmtDistance(haversine(a, b)) }, geometry: { type: 'Point', coordinates: mid } })
  }
  const labelSrc = map.getSource(MEASURE_LABEL_SOURCE) as mapboxgl.GeoJSONSource | undefined
  labelSrc?.setData({ type: 'FeatureCollection', features: labels })
}

function updateMeasurePopup (map: mapboxgl.Map) {
  const pts = measureDisplayPoints()
  if (pts.length < 2) { measurePopup?.remove(); measurePopup = null; return }
  const total = pathLength(pts)
  const anchor = pts[pts.length - 1]
  const html = `<div style="font-size:12px; white-space:nowrap;">${fmtDistance(total)}${measureFinalized ? '' : ' <span style="opacity:.6;">(double-clic pour terminer)</span>'}</div>`
  if (!measurePopup) {
    measurePopup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 12, className: 'measure-popup' })
  }
  measurePopup.setLngLat(anchor).setHTML(html).addTo(map)
}

function startMeasure (map: mapboxgl.Map) {
  measureActive = true
  measureFinalized = false
  measurePoints = []
  measureCursor = null
  ensureMeasureLayers(map)
  updateMeasureSource(map)
  map.getCanvas().style.cursor = 'crosshair'
  map.doubleClickZoom.disable()

  measureClickHandler = (e) => {
    if (measureFinalized) return
    measurePoints.push([e.lngLat.lng, e.lngLat.lat])
    updateMeasureSource(map)
    updateMeasurePopup(map)
  }
  measureMoveHandler = (e) => {
    if (measureFinalized) return
    measureCursor = [e.lngLat.lng, e.lngLat.lat]
    if (measurePoints.length >= 1) { updateMeasureSource(map); updateMeasurePopup(map) }
  }
  measureDblHandler = () => {
    // Le double-clic émet 2 « click » : on retire le sommet dupliqué puis on fige.
    if (measurePoints.length >= 2) measurePoints.pop()
    measureFinalized = true
    measureCursor = null
    map.getCanvas().style.cursor = ''
    updateMeasureSource(map)
    updateMeasurePopup(map)
  }
  measureKeyHandler = (e) => { if (e.key === 'Escape') stopMeasure(map) }
  // setStyle efface source/layers : on les recrée si un changement de fond survient.
  measureStyleHandler = () => { if (measureActive) { ensureMeasureLayers(map); updateMeasureSource(map) } }

  map.on('click', measureClickHandler)
  map.on('mousemove', measureMoveHandler)
  map.on('dblclick', measureDblHandler)
  map.on('style.load', measureStyleHandler)
  document.addEventListener('keydown', measureKeyHandler)

  measureBtnEl?.classList.add('measure-btn-active')
}

function stopMeasure (map: mapboxgl.Map) {
  measureActive = false
  measureFinalized = false
  measurePoints = []
  measureCursor = null
  if (measureClickHandler) map.off('click', measureClickHandler)
  if (measureMoveHandler) map.off('mousemove', measureMoveHandler)
  if (measureDblHandler) map.off('dblclick', measureDblHandler)
  if (measureStyleHandler) map.off('style.load', measureStyleHandler)
  if (measureKeyHandler) document.removeEventListener('keydown', measureKeyHandler)
  measureClickHandler = measureMoveHandler = measureDblHandler = null
  measureStyleHandler = null
  measureKeyHandler = null
  measurePopup?.remove(); measurePopup = null
  const src = map.getSource(MEASURE_SOURCE) as mapboxgl.GeoJSONSource | undefined
  src?.setData({ type: 'FeatureCollection', features: [] })
  const labelSrc = map.getSource(MEASURE_LABEL_SOURCE) as mapboxgl.GeoJSONSource | undefined
  labelSrc?.setData({ type: 'FeatureCollection', features: [] })
  map.getCanvas().style.cursor = ''
  map.doubleClickZoom.enable()
  measureBtnEl?.classList.remove('measure-btn-active')
}

function toggleMeasure (map: mapboxgl.Map) {
  if (measureActive) stopMeasure(map)
  else startMeasure(map)
}

class MapExtraControls implements mapboxgl.IControl {
  private _map: mapboxgl.Map | null = null
  private _container: HTMLDivElement | null = null

  onAdd (map: mapboxgl.Map) {
    this._map = map
    this._container = document.createElement('div')
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'

    // Center on drone
    this._container.appendChild(makeCtrlButton('Centrer sur le drone', '✈', () => {
      const markers = store.state.markerInfo.coverMap
      const activeSn = store.state.osdVisible?.sn
      let marker = activeSn ? markers[activeSn] : null
      if (!marker) {
        const firstSn = Object.keys(markers)[0]
        marker = firstSn ? markers[firstSn] : null
      }
      if (marker) map.flyTo({ center: marker.getLngLat(), zoom: Math.max(map.getZoom(), 15), speed: 1.5 })
    }))

    // Center on RC (gateway)
    this._container.appendChild(makeCtrlButton('Centrer sur le télépilote', '🎮', () => {
      const markers = store.state.markerInfo.coverMap
      const gatewaySn = store.state.osdVisible?.gateway_sn
      let marker = gatewaySn ? markers[gatewaySn] : null
      if (!marker) {
        // Fallback: find any gateway marker
        const gatewaySns = Object.keys(store.state.deviceState.gatewayInfo)
        marker = gatewaySns.length ? markers[gatewaySns[0]] : null
      }
      if (marker) map.flyTo({ center: marker.getLngLat(), zoom: Math.max(map.getZoom(), 15), speed: 1.5 })
    }))

    // Go to coordinates
    this._container.appendChild(makeCtrlButton('Aller à des coordonnées', '🔍', () => {
      // Toggle the coordinate input panel
      const existing = document.getElementById('coord-search-panel')
      if (existing) { existing.remove(); return }

      const panel = document.createElement('div')
      panel.id = 'coord-search-panel'
      panel.style.cssText = [
        'position:absolute',
        'top:0',
        'left:36px',
        'background:#1a1a1a',
        'border:1px solid #444',
        'border-radius:4px',
        'padding:8px',
        'display:flex',
        'flex-direction:column',
        'gap:6px',
        'z-index:9999',
        'min-width:240px',
        'box-shadow:0 2px 8px rgba(0,0,0,0.5)',
      ].join(';')

      const label = document.createElement('span')
      label.textContent = 'Coordonnées (lat, lon)'
      label.style.cssText = 'color:#ccc; font-size:11px;'

      const input = document.createElement('input')
      input.type = 'text'
      input.placeholder = '48.8534, 2.3488'
      input.style.cssText = [
        'background:#2a2a2a',
        'border:1px solid #555',
        'border-radius:3px',
        'color:#fff',
        'font-size:12px',
        'padding:4px 6px',
        'outline:none',
        'width:100%',
        'box-sizing:border-box',
      ].join(';')

      // Second champ : coordonnées en degrés/minutes/secondes (DMS).
      // Saisie structurée — un select N/S ou E/O + une case par nombre (deg/min/sec),
      // pour éviter d'avoir à taper les symboles ° ' ".
      const dmsLabel = document.createElement('span')
      dmsLabel.textContent = 'ou DMS'
      dmsLabel.style.cssText = 'color:#ccc; font-size:11px;'

      const dmsFieldStyle = [
        'background:#2a2a2a',
        'border:1px solid #555',
        'border-radius:3px',
        'color:#fff',
        'font-size:12px',
        'padding:4px 2px',
        'outline:none',
        'box-sizing:border-box',
        'text-align:center',
      ].join(';')
      const makeDmsNum = (ph: string, max: number) => {
        const el = document.createElement('input')
        el.type = 'number'
        el.min = '0'
        el.max = String(max)
        el.step = 'any'
        el.placeholder = ph
        el.style.cssText = dmsFieldStyle + ';width:44px'
        return el
      }
      const makeDmsUnit = (t: string) => {
        const s = document.createElement('span')
        s.textContent = t
        s.style.cssText = 'color:#ccc; font-size:12px;'
        return s
      }
      const makeDmsHemi = (opts: string[]) => {
        const sel = document.createElement('select')
        sel.style.cssText = dmsFieldStyle
        for (const o of opts) {
          const opt = document.createElement('option')
          opt.value = o
          opt.textContent = o
          sel.appendChild(opt)
        }
        return sel
      }

      // Ligne latitude : hémisphère + degrés/minutes/secondes
      const latRow = document.createElement('div')
      latRow.style.cssText = 'display:flex; align-items:center; gap:3px;'
      const latHemi = makeDmsHemi(['N', 'S'])
      const latDeg = makeDmsNum('48', 90)
      const latMin = makeDmsNum('51', 59)
      const latSec = makeDmsNum('21', 60)
      latRow.append(latHemi, latDeg, makeDmsUnit('°'), latMin, makeDmsUnit("'"), latSec, makeDmsUnit('"'))

      // Ligne longitude
      const lonRow = document.createElement('div')
      lonRow.style.cssText = 'display:flex; align-items:center; gap:3px;'
      const lonHemi = makeDmsHemi(['E', 'O'])
      const lonDeg = makeDmsNum('2', 180)
      const lonMin = makeDmsNum('20', 59)
      const lonSec = makeDmsNum('56', 60)
      lonRow.append(lonHemi, lonDeg, makeDmsUnit('°'), lonMin, makeDmsUnit("'"), lonSec, makeDmsUnit('"'))

      const errorMsg = document.createElement('span')
      errorMsg.style.cssText = 'color:#ff4d4f; font-size:11px; display:none;'

      const btn = document.createElement('button')
      btn.textContent = 'Centrer'
      btn.style.cssText = [
        'background:#1890ff',
        'border:none',
        'border-radius:3px',
        'color:#fff',
        'cursor:pointer',
        'font-size:12px',
        'padding:4px 8px',
        'width:100%',
      ].join(';')

      const showError = (msg: string) => { errorMsg.textContent = msg; errorMsg.style.display = 'block' }

      // Vole vers la position, dépose une pin (altitude terrain réelle) puis ferme.
      // Rose vif : couleur absente des pins de la RC (DJI Pilot 2) → un pin déposé
      // depuis la recherche ne se confond pas avec un pin remonté par la radiocommande.
      const SEARCH_PIN_COLOR = '#FF3EA5'

      const goTo = (lat: number, lon: number) => {
        errorMsg.style.display = 'none'
        map.flyTo({ center: [lon, lat], zoom: Math.max(map.getZoom(), 15), speed: 1.5 })
        const id = uuidv4()
        // Nom horodaté à largeur fixe → tri par nom = ordre de création.
        const d = new Date()
        const p = (n: number) => String(n).padStart(2, '0')
        const name = `Pin ${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
        const terrainH = (map as any).queryTerrainElevation({ lng: lon, lat }, { exaggerated: false }) ?? 0
        const content = generatePoint(
          { height: terrainH, latitude: lat, longitude: lon },
          { color: SEARCH_PIN_COLOR, clampToGround: true }
        )
        useGMapCover().init2DPin(name, [lon, lat, terrainH], SEARCH_PIN_COLOR, { id, name })
        const targetGroupId = store.state.activeGroupId || (store.state.layerBaseInfo as any)?.share
        if (targetGroupId) {
          postElementsReq(targetGroupId, { id, name, resource: { type: MapElementEnum.PIN, content } } as any)
            .then(() => EventBus.emit('mapGroupRefresh'))
        }
        panel.remove()
      }

      const flyToCoords = () => {
        const raw = input.value.trim()
        if (raw) {
          // Décimal : "lat, lon" ou "lat lon" (symboles de degré tolérés)
          const match = raw.replace(/[°′″'"]/g, '').match(/^(-?\d+(?:[.,]\d+)?)\s*[,;\s]\s*(-?\d+(?:[.,]\d+)?)$/)
          if (!match) { showError('Format décimal invalide. Ex: 48.8534, 2.3488'); return }
          const lat = parseFloat(match[1].replace(',', '.'))
          const lon = parseFloat(match[2].replace(',', '.'))
          if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
            showError('Format décimal invalide. Ex: 48.8534, 2.3488'); return
          }
          goTo(lat, lon); return
        }
        // DMS structuré : hémisphère + degrés/minutes/secondes
        const dmsFields = [latDeg, latMin, latSec, lonDeg, lonMin, lonSec]
        if (dmsFields.some((el) => el.value.trim() !== '')) {
          const num = (el: HTMLInputElement) => {
            const v = parseFloat(el.value.replace(',', '.'))
            return isNaN(v) ? 0 : v
          }
          let lat = num(latDeg) + num(latMin) / 60 + num(latSec) / 3600
          let lon = num(lonDeg) + num(lonMin) / 60 + num(lonSec) / 3600
          if (latHemi.value === 'S') lat = -lat
          if (lonHemi.value === 'O' || lonHemi.value === 'W') lon = -lon
          if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
            showError('Coordonnées DMS hors limites.'); return
          }
          goTo(lat, lon); return
        }
        showError('Saisir des coordonnées (décimal ou DMS).')
      }

      btn.addEventListener('click', flyToCoords)
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Enter') flyToCoords()
        if (e.key === 'Escape') panel.remove()
      }
      input.addEventListener('keydown', onKey)
      for (const el of [latHemi, latDeg, latMin, latSec, lonHemi, lonDeg, lonMin, lonSec]) {
        el.addEventListener('keydown', onKey)
      }

      // Passage automatique à la case suivante quand un nombre entier atteint sa
      // largeur (deg = 2 ou 3 chiffres, min = 2). On laisse les secondes libres
      // pour permettre les décimales (ex. 21.4").
      const dmsAdvance = [
        { el: latDeg, next: latMin, digits: 2 },
        { el: latMin, next: latSec, digits: 2 },
        { el: lonDeg, next: lonMin, digits: 3 },
        { el: lonMin, next: lonSec, digits: 2 },
      ]
      for (const { el, next, digits } of dmsAdvance) {
        el.addEventListener('input', () => {
          const v = el.value
          if (/^\d+$/.test(v) && v.length >= digits) next.focus()
        })
      }

      panel.appendChild(label)
      panel.appendChild(input)
      panel.appendChild(dmsLabel)
      panel.appendChild(latRow)
      panel.appendChild(lonRow)
      panel.appendChild(errorMsg)
      panel.appendChild(btn)

      this._container!.style.position = 'relative'
      this._container!.appendChild(panel)
      setTimeout(() => input.focus(), 0)
    }))

    // Center on user position
    this._container.appendChild(makeCtrlButton('Centrer sur ma position', '📍', () => {
      if (!navigator.geolocation) return
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords: [number, number] = [pos.coords.longitude, pos.coords.latitude]
          lastUserPosition = coords
          map.flyTo({ center: coords, zoom: Math.max(map.getZoom(), 15), speed: 1.5 })
        },
        () => {
          // Permission refusée ou timeout → fallback sur dernière position connue
          if (lastUserPosition) map.flyTo({ center: lastUserPosition, zoom: Math.max(map.getZoom(), 15), speed: 1.5 })
        },
        { enableHighAccuracy: true, timeout: 5000 }
      )
    }))

    // Measure tool (non-persisted linestring)
    measureBtnEl = makeCtrlButton('Mesurer une distance', '📏', () => toggleMeasure(map))
    if (measureActive) measureBtnEl.classList.add('measure-btn-active')
    this._container.appendChild(measureBtnEl)

    return this._container
  }

  onRemove () {
    this._container?.parentNode?.removeChild(this._container)
    this._map = null
  }
}

// Last known user position — persists across style changes
let lastUserPosition: [number, number] | null = null

function addUserLocationSourceAndLayer (map: mapboxgl.Map) {
  const size = 120

  const pulsingDot = {
    width: size,
    height: size,
    data: new Uint8Array(size * size * 4),
    context: null as CanvasRenderingContext2D | null,

    onAdd () {
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      this.context = canvas.getContext('2d')
    },

    render () {
      const t = (performance.now() / 1000) % 1
      const radius = (size / 2) * 0.25
      const outerRadius = (size / 2) * 0.7 * t + radius
      const ctx = this.context!
      ctx.clearRect(0, 0, size, size)

      ctx.beginPath()
      ctx.arc(size / 2, size / 2, outerRadius, 0, Math.PI * 2)
      ctx.fillStyle = `rgba(41, 128, 240, ${1 - t})`
      ctx.fill()

      ctx.beginPath()
      ctx.arc(size / 2, size / 2, radius, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(41, 128, 240, 1)'
      ctx.strokeStyle = 'white'
      ctx.lineWidth = 3
      ctx.fill()
      ctx.stroke()

      this.data = ctx.getImageData(0, 0, size, size).data as unknown as Uint8Array
      map.triggerRepaint()
      return true
    }
  }

  if (!map.hasImage('user-location-dot')) {
    map.addImage('user-location-dot', pulsingDot as any, { pixelRatio: 2 })
  }

  if (!map.getSource('user-location')) {
    const initialCoords = lastUserPosition || [0, 0]
    map.addSource('user-location', {
      type: 'geojson',
      data: { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: initialCoords } }
    })
  }

  if (!map.getLayer('user-location-layer')) {
    map.addLayer({
      id: 'user-location-layer',
      type: 'symbol',
      source: 'user-location',
      layout: { 'icon-image': 'user-location-dot', 'icon-allow-overlap': true },
    })
  }
}

function addUserLocationMarker (map: mapboxgl.Map) {
  addUserLocationSourceAndLayer(map)

  function updatePosition (pos: { coords: { longitude: number, latitude: number } }) {
    const coords: [number, number] = [pos.coords.longitude, pos.coords.latitude]
    lastUserPosition = coords
    const src = map.getSource('user-location') as mapboxgl.GeoJSONSource | undefined
    if (src) {
      src.setData({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: coords } })
    }
  }

  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(updatePosition, () => {})
    const watchId = navigator.geolocation.watchPosition(updatePosition, () => {})
    map.once('remove', () => navigator.geolocation.clearWatch(watchId))
  }

  // Re-add the full 3D scenery after style changes (setStyle wipes sources/layers)
  map.on('style.load', () => {
    addUserLocationSourceAndLayer(map)
    add3DScenery(map)
  })
}

export function useGMapManage () {
  async function initMap (container: string, app: App, onLoad?: () => void) {
    mapboxgl.accessToken = CURRENT_CONFIG.mapboxToken

    const map = new mapboxgl.Map({
      container,
      style: 'mapbox://styles/mapbox/satellite-streets-v12',
      center: [3.57, 47.85],
      zoom: 9,
      maxPitch: 75,
    })

    const draw = new MapboxDraw({
      displayControlsDefault: false,
    })

    map.addControl(draw)
    map.addControl(new mapboxgl.NavigationControl(), 'top-left')
    map.addControl(new MapExtraControls(), 'top-left')
    map.addControl(new mapboxgl.ScaleControl({ unit: 'metric' }), 'bottom-right')

    map.once('load', () => {
      app.config.globalProperties.$map = map
      app.config.globalProperties.$draw = draw
      EventBus.emit('mapLoaded')
      addUserLocationMarker(map)
      add3DScenery(map)
      onLoad?.()
    })
  }

  function globalPropertiesConfig (app: App, onLoad?: () => void) {
    initMap('g-container', app, onLoad)
  }

  return {
    globalPropertiesConfig,
  }
}
